import { inject, Injectable } from '@angular/core';
import Keycloak from 'keycloak-js';
import { AUTH_SYNC_CHANNEL, AuthSyncMessage, SharedTokens } from '../init/cross-tab-auth';

/**
 * Keeps Keycloak authentication in sync across browser tabs.
 *
 * - Answers a newly-opened tab's `request-tokens` with this tab's current
 *   tokens, so the new tab adopts them instead of redirecting through Keycloak
 *   (which would create a spurious login-attempt row). See {@link requestTokensFromPeers}.
 * - Propagates logout: signing out in one tab signs out the others.
 *
 * Tokens are *not* persisted anywhere — they only ever travel between live tabs
 * over a {@link BroadcastChannel}, so nothing sensitive is left at rest.
 *
 * Refresh-token rotation is disabled in the realm (`revokeRefreshToken: false`),
 * so tabs can independently refresh the shared refresh token without
 * invalidating one another; no live token re-sync is required.
 *
 * This service must be constructed eagerly (see app.config) so every open tab is
 * listening and able to answer the next new tab.
 */
@Injectable({ providedIn: 'root' })
export class AuthSyncService {
  private readonly keycloak = inject(Keycloak);
  private readonly channel =
    typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(AUTH_SYNC_CHANNEL) : null;

  /** Guards against re-broadcasting a logout we received from a peer. */
  private handlingPeerLogout = false;

  constructor() {
    this.channel?.addEventListener('message', this.onMessage);
  }

  /**
   * Broadcast a logout to peer tabs, then log out locally. Call this instead of
   * `keycloak.logout()` so every tab signs out together.
   */
  logout(): void {
    this.channel?.postMessage({ type: 'logout' } satisfies AuthSyncMessage);
    this.keycloak.logout();
  }

  private onMessage = (event: MessageEvent<AuthSyncMessage>): void => {
    const message = event.data;
    if (!message) {
      return;
    }

    switch (message.type) {
      case 'request-tokens': {
        const tokens = this.currentTokens();
        if (tokens) {
          this.channel?.postMessage(
            { type: 'tokens', nonce: message.nonce, tokens } satisfies AuthSyncMessage,
          );
        }
        break;
      }
      case 'logout': {
        if (this.handlingPeerLogout) {
          break;
        }
        this.handlingPeerLogout = true;
        this.keycloak.logout();
        break;
      }
    }
  };

  private currentTokens(): SharedTokens | null {
    const { token, refreshToken, idToken } = this.keycloak;
    if (!token || !refreshToken) {
      return null;
    }
    return { token, refreshToken, idToken };
  }
}
